/**
 * Shopping Request real-time – Ably.
 * Subscribes to shopping_request:${requestId} and user:${userId}.
 * See docs/ABLY_CUSTOMER_PORTAL.md.
 */
import { subscribeToChannel } from './ablyRealtime';
import { getUserIdFromToken } from '@/utils/jwt';
import { apiService } from './api';

const REQUEST_CHANNEL_EVENTS = [
  'shopping_request_message:new',
  'shopping_request_message:reaction_added',
  'shopping_request_message:reaction_removed',
] as const;

/**
 * Events delivered on the personal `user:${uid}` channel.
 * The backend now publishes incoming chat messages (organizer → customer) and
 * cart-order status updates ONLY to this channel (see cross-repo contract), so
 * the customer portal MUST subscribe here to receive live chat + order updates.
 * The sender no longer receives an echo of their own message.
 */
const USER_EVENTS = [
  'shopping_request:approved',
  'shopping_request:rejected',
  'shopping_request:checked_in',
  'shopping_request:completed',
  'shopping_request:cancelled',
  'shopping_request_message:notification',
  'shopping_request_message:new',
  // Organizer typing arrives on the customer's personal channel (directed the
  // same way as messages so the customer never echoes its own typing).
  'shopping_request:typing',
  'shopping_request_cart_order:created',
  'shopping_request_cart_order:updated',
] as const;

export class ShoppingRequestWebSocketService {
  private eventHandlers: Map<string, Set<Function>> = new Map();
  private unsubRequest: (() => void) | null = null;
  private unsubUser: (() => void) | null = null;
  private currentRequestId: string | null = null;
  private lastMessageId: string | null = null;
  private lastMessageTime = 0;

  connect(requestId: string): void {
    if (this.currentRequestId === requestId && this.unsubRequest) return;
    this.disconnect();

    const { accessToken } = apiService.getTokens();
    if (!accessToken) {
      console.warn('[ShoppingRequestWS] No access token');
      return;
    }

    const uid = getUserIdFromToken(accessToken);
    if (!uid) {
      console.warn('[ShoppingRequestWS] No userId from token');
      return;
    }

    this.currentRequestId = requestId;

    const emit = (event: string, data: any) => {
      this.eventHandlers.get(event)?.forEach((h) => {
        try {
          h(data);
        } catch (e) {
          console.error('[ShoppingRequestWS] handler error', e);
        }
      });
    };

    const processNewMessage = (data: any) => {
      const id = data?.id;
      const now = Date.now();
      if (id && id === this.lastMessageId && now - this.lastMessageTime < 500) return;
      this.lastMessageId = id ?? null;
      this.lastMessageTime = now;
      emit('shopping_request_message:new', data);
    };

    this.unsubRequest = subscribeToChannel(
      `shopping_request:${requestId}`,
      REQUEST_CHANNEL_EVENTS as unknown as readonly string[],
      (event, data) => {
        if (event === 'shopping_request_message:new') processNewMessage(data);
        else emit(event, data);
      }
    );

    this.unsubUser = subscribeToChannel(
      `user:${uid}`,
      USER_EVENTS as unknown as readonly string[],
      (event, data) => {
        // Incoming chat messages now arrive on the user channel; dedup against
        // any copy that might also arrive on the request channel.
        if (event === 'shopping_request_message:new') processNewMessage(data);
        else emit(event, data);
      }
    );
  }

  leaveRequestRoom(_requestId: string): void {
    this.disconnect();
  }

  on(event: string, handler: Function): void {
    if (!this.eventHandlers.has(event)) this.eventHandlers.set(event, new Set());
    this.eventHandlers.get(event)!.add(handler);
  }

  off(event: string, handler: Function): void {
    this.eventHandlers.get(event)?.delete(handler);
  }

  isConnected(): boolean {
    return this.unsubRequest != null && this.currentRequestId != null;
  }

  disconnect(): void {
    if (this.unsubRequest) {
      this.unsubRequest();
      this.unsubRequest = null;
    }
    if (this.unsubUser) {
      this.unsubUser();
      this.unsubUser = null;
    }
    this.currentRequestId = null;
    this.eventHandlers.clear();
  }
}

export const shoppingRequestWebSocketService = new ShoppingRequestWebSocketService();
